import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Search,
  Edit,
  Trash2,
  Eye,
  Download,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Check,
  X,
  RefreshCw
} from 'lucide-react';
import Button from '../../../components/common/Button';
import Card from '../../../components/ui/Card';
import DataTable from '../../../components/ui/DataTable';
import Input from '../../../components/common/Input';
import Select from '../../../components/common/Select';
import Badge from '../../../components/common/Badge';
import AdminProductService from './admin.product.service';
import ConfirmModal from '../../../components/ConfirmModal';
import Toast from '../../../components/Toast';

const ProductsPage = () => {
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToAction, setProductToAction] = useState(null);
  const [actionType, setActionType] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      console.log('📦 Fetching products from AdminProductService...');
      const productsData = await AdminProductService.getAllProducts();
      console.log('✅ Products fetched successfully:', productsData);
      
      // Ensure productsData is an array
      if (Array.isArray(productsData)) {
        console.log(`✅ Loaded ${productsData.length} products`);
        setProducts(productsData);
      } else {
        console.error('❌ Products data is not an array:', productsData);
        setProducts([]);
        setToastMessage('Invalid products data format received');
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      setToastMessage(`Failed to load products: ${error.message}`);
      setToastType('error');
      setShowToast(true);
      setProducts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return (
          <Badge variant="warning" size="sm">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'APPROVED':
        return (
          <Badge variant="success" size="sm">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge variant="danger" size="sm">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="gray" size="sm">
            {status}
          </Badge>
        );
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name?.toLowerCase().includes(search.toLowerCase()) ||
      product.category?.toLowerCase().includes(search.toLowerCase()) ||
      product.sellerName?.toLowerCase().includes(search.toLowerCase()) ||
      product.sellerEmail?.toLowerCase().includes(search.toLowerCase()) ||
      product.hsCode?.toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = filter === 'all' || product.status === filter;
    
    return matchesSearch && matchesFilter;
  });

  const handleSelectProduct = (productId, checked) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleViewProduct = (id) => {
    navigate(`/admin/products/${id}`);
  };

  const handleEditProduct = (id) => {
    navigate(`/admin/products/${id}/edit`);
  };

  const handleProductAction = (product, action) => {
    setProductToAction(product);
    setActionType(action);
    
    if (action === 'reject') {
      setRejectionReason('');
    }
    
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    try {
      if (actionType === 'approve') {
        await AdminProductService.approveProduct(productToAction.id);
        setToastMessage(`Product "${productToAction.name}" approved successfully!`);
        setToastType('success');
      } else if (actionType === 'reject') {
        if (!rejectionReason.trim()) {
          setToastMessage('Please provide a rejection reason');
          setToastType('error');
          setShowToast(true);
          return;
        }
        
        await AdminProductService.rejectProduct(productToAction.id, rejectionReason);
        setToastMessage(`Product "${productToAction.name}" rejected successfully!`);
        setToastType('success');
      } else if (actionType === 'delete') {
        await AdminProductService.deleteProduct(productToAction.id);
        setToastMessage(`Product "${productToAction.name}" deleted successfully!`);
        setToastType('success');
      }
      
      // Refresh products list
      fetchProducts();
      setSelectedProducts(selectedProducts.filter(id => id !== productToAction.id));
      
    } catch (error) {
      setToastMessage(`Failed to perform action: ${error.message}`);
      setToastType('error');
    } finally {
      setShowConfirmModal(false);
      setProductToAction(null);
      setActionType('');
      setRejectionReason('');
      setShowToast(true);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedProducts.length === 0) return;
    
    try {
      for (const productId of selectedProducts) {
        await AdminProductService.approveProduct(productId);
      }
      
      setToastMessage(`${selectedProducts.length} products approved successfully!`);
      setToastType('success');
      setShowToast(true);
      fetchProducts();
      setSelectedProducts([]);
    } catch (error) {
      setToastMessage(`Failed to approve products: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleBulkReject = async () => {
    if (selectedProducts.length === 0) return;
    
    const reason = prompt('Enter rejection reason for all selected products:');
    if (!reason || !reason.trim()) {
      setToastMessage('Rejection reason is required');
      setToastType('error');
      setShowToast(true);
      return;
    }
    
    try {
      for (const productId of selectedProducts) {
        await AdminProductService.rejectProduct(productId, reason);
      }
      
      setToastMessage(`${selectedProducts.length} products rejected successfully!`);
      setToastType('success');
      setShowToast(true);
      fetchProducts();
      setSelectedProducts([]);
    } catch (error) {
      setToastMessage(`Failed to reject products: ${error.message}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleExport = () => {
    console.log('Export products:', selectedProducts);
    // Add export functionality here
  };

  const columns = [
    {
      id: 'select',
      header: (
        <input
          type="checkbox"
          checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      accessor: 'id',
      render: (item) => (
        <input
          type="checkbox"
          checked={selectedProducts.includes(item.id)}
          onChange={(e) => handleSelectProduct(item.id, e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
      width: '50px'
    },
    {
      id: 'product',
      header: 'Product Details',
      accessor: 'name',
      render: (item) => (
        <div className="flex items-center">
          <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
            {item.images && item.images.length > 0 && item.images[0]?.data ? (
              <img 
                src={item.images[0].data} 
                alt={item.name}
                className="h-full w-full object-cover rounded-lg"
              />
            ) : (
              <Package className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center">
              <div className="font-semibold text-gray-900 truncate">{item.name}</div>
              {item.hsCode && (
                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  HS: {item.hsCode}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 truncate">{item.category}</div>
            <div className="text-xs text-gray-400 mt-1">
              Seller: {item.sellerName || item.sellerEmail || 'Unknown'}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'specs',
      header: 'Specifications',
      render: (item) => (
        <div className="max-w-xs">
          <div className="text-sm font-medium text-gray-900">
            {item.quantity} {item.unit}
          </div>
          <div className="text-sm text-gray-500">
            ₹{item.price} {item.currency}
          </div>
          {item.specifications && item.specifications.length > 0 && (
            <div className="text-xs text-gray-400 mt-1 truncate">
              {item.specifications[0]}
              {item.specifications.length > 1 && ` +${item.specifications.length - 1} more`}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      render: (item) => (
        <div className="space-y-1">
          {getStatusBadge(item.status)}
          {item.status === 'REJECTED' && item.rejectionReason && (
            <div className="text-xs text-red-600 max-w-xs truncate" title={item.rejectionReason}>
              <AlertCircle className="h-3 w-3 inline mr-1" />
              {item.rejectionReason}
            </div>
          )}
          {item.status === 'APPROVED' && item.approvedByEmail && (
            <div className="text-xs text-gray-500">
              Approved by: {item.approvedByEmail}
            </div>
          )}
        </div>
      )
    },
    {
      id: 'dates',
      header: 'Dates',
      render: (item) => (
        <div className="text-sm">
          <div className="text-gray-900">
            Created: {item.createdAt?.toLocaleDateString?.() || 'N/A'}
          </div>
          <div className="text-gray-500">
            Updated: {item.updatedAt?.toLocaleDateString?.() || 'N/A'}
          </div>
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (item) => (
        <div className="flex items-center space-x-2">
          <Button
            size="xs"
            variant="ghost"
            onClick={() => handleViewProduct(item.id)}
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </Button>
          
          {item.status === 'PENDING' && (
            <>
              <Button
                size="xs"
                variant="success"
                onClick={() => handleProductAction(item, 'approve')}
                title="Approve Product"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="xs"
                variant="danger"
                onClick={() => handleProductAction(item, 'reject')}
                title="Reject Product"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
          
          {(item.status === 'APPROVED' || item.status === 'REJECTED') && (
            <Button
              size="xs"
              variant="warning"
              onClick={() => handleProductAction(item, 'delete')}
              title="Delete Product"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          
          {/* <Button
            size="xs"
            variant="outline"
            onClick={() => handleEditProduct(item.id)}
            title="Edit Product"
          >
            <Edit className="h-4 w-4" />
          </Button> */}
        </div>
      )
    }
  ];

  const getModalConfig = () => {
    switch (actionType) {
      case 'approve':
        return {
          title: 'Approve Product',
          message: `Are you sure you want to approve "${productToAction?.name}"? This will make it visible to all users.`,
          confirmText: 'Approve',
          variant: 'success'
        };
      case 'reject':
        return {
          title: 'Reject Product',
          message: (
            <div className="space-y-3">
              <p>Are you sure you want to reject "{productToAction?.name}"?</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection Reason (required):
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Please provide a reason for rejection..."
                  required
                />
              </div>
            </div>
          ),
          confirmText: 'Reject',
          variant: 'danger',
          disabled: !rejectionReason.trim()
        };
      case 'delete':
        return {
          title: 'Delete Product',
          message: `Are you sure you want to delete "${productToAction?.name}"? This action cannot be undone.`,
          confirmText: 'Delete',
          variant: 'danger'
        };
      default:
        return { title: '', message: '', confirmText: 'Confirm' };
    }
  };

  // Main content
  const pendingCount = products.filter(p => p.status === 'PENDING').length;
  const approvedCount = products.filter(p => p.status === 'APPROVED').length;
  const rejectedCount = products.filter(p => p.status === 'REJECTED').length;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setProductToAction(null);
          setActionType('');
          setRejectionReason('');
        }}
        onConfirm={confirmAction}
        title={getModalConfig().title}
        message={getModalConfig().message}
        confirmText={getModalConfig().confirmText}
        variant={getModalConfig().variant}
        disabled={getModalConfig().disabled}
      />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Product Approval Management</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Review and manage all products submitted by sellers
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="small"
            onClick={fetchProducts}
            loading={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="small" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{products.length}</p>
            </div>
            <Package className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
        
        <Card className="border-l-4 border-l-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{pendingCount}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{approvedCount}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </Card>
        
        <Card className="border-l-4 border-l-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{rejectedCount}</p>
            </div>
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
        </Card>
      </div> */}

      {/* Bulk Actions */}
      {selectedProducts.length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-blue-600">Choose an action to perform on selected products</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="small"
                variant="success"
                onClick={handleBulkApprove}
              >
                <Check className="h-4 w-4 mr-2" />
                Approve Selected
              </Button>
              <Button
                size="small"
                variant="danger"
                onClick={handleBulkReject}
              >
                <X className="h-4 w-4 mr-2" />
                Reject Selected
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters Card */}
      <Card>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search products by name, category, seller, or HS code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'PENDING', label: 'Pending Review' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'REJECTED', label: 'Rejected' }
              ]}
              className="min-w-[160px]"
            />
            
            <Button variant="outline" size="small">
              <Filter className="h-4 w-4 mr-2" />
              Advanced
            </Button>
          </div>
        </div>
      </Card>

      {/* Products Table */}
      {loading ? (
        <Card className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        </Card>
      ) : filteredProducts.length === 0 ? (
        <Card className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
          <Button variant="outline" onClick={() => {
            setSearch('');
            setFilter('all');
          }}>
            Clear all filters
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <DataTable
              columns={columns}
              data={filteredProducts}
              loading={loading}
              itemsPerPage={10}
              emptyMessage="No products found. Try adjusting your filters."
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-700 mb-2 sm:mb-0">
              Showing <span className="font-semibold">1-{Math.min(filteredProducts.length, 10)}</span> of{' '}
              <span className="font-semibold">{filteredProducts.length}</span> products
            </p>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="xs" disabled>
                Previous
              </Button>
              <Button variant="outline" size="xs">
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ProductsPage;